import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { MarkdownMessage } from "../components/MarkdownMessage";
import { useI18n } from "../i18n";
import { LocalHistoryDrawer, isTaskResumable } from "./LocalHistoryDrawer";
import { LocalSettingsModal } from "./LocalSettingsModal";
import type { AgentType, DeviceTarget, LocalTask, LocalTaskMessage, SessionEvent } from "../types/api";
import { failureText } from "../lib/failure";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  streaming?: boolean;
}

interface PendingApproval {
  id: string;
  title: string;
  body: string;
}

interface Props {
  drawerOpen: boolean;
  settingsOpen: boolean;
  onCloseDrawer: () => void;
  onCloseSettings: () => void;
  // Bumped by the header's "new chat" button: clear the pane and start fresh.
  resetSignal: number;
}

const STREAM_WATCHDOG_MS = 15 * 60_000;

function eventText(event: SessionEvent): string {
  const p = event.payload as { text?: string; message?: string };
  return p?.text ?? p?.message ?? "";
}

// Same noise filter as mole's restore view: drop tool-call plumbing so a
// restored transcript reads as just the prompts and the answers.
const NOISE_MARKERS = new Set(["tool_result", "tool_use", "thinking", "image"]);

function transcriptBody(message: LocalTaskMessage): string {
  if (NOISE_MARKERS.has((message.label ?? "").trim().toLowerCase())) return "";
  return message.body
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) return false;
      if (NOISE_MARKERS.has(trimmed.toLowerCase())) return false;
      return !trimmed.startsWith("Tool use:");
    })
    .join("\n")
    .trim();
}

// A local task's provider, as the agent kind the relay understands.
function taskAgent(task: LocalTask): AgentType {
  return task.provider === "claude" ? "claude-code" : "codex";
}

/**
 * Drive a local-device agent: pick an online paired device, then chat. The
 * history drawer lists the device's recent local tasks — picking one loads its
 * transcript and continues it on the device; "new chat" starts a fresh thread.
 * Pairing lives in Settings.
 */
export function LocalAgentScreen({ drawerOpen, settingsOpen, onCloseDrawer, onCloseSettings, resetSignal }: Props) {
  const { client, account } = useAuth();
  const { t } = useI18n();

  const [devices, setDevices] = useState<DeviceTarget[]>([]);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [agent, setAgent] = useState<AgentType>("codex");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<LocalTask[]>([]);

  // The local task being viewed/continued; null = a fresh chat.
  const [activeTask, setActiveTask] = useState<LocalTask | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const selected = devices.find((d) => d.id === deviceId) ?? null;
  const canContinue = activeTask === null || isTaskResumable(activeTask);

  const loadDevices = useCallback(async () => {
    if (!client || !account) return;
    try {
      const { localDevices } = await client.listAgents(account.id);
      setDevices(localDevices);
      setError(null);
      // Auto-select the first online device if none is chosen.
      setDeviceId((cur) => cur ?? localDevices.find((d) => d.online)?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("agent.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [client, account, t]);

  useEffect(() => {
    void loadDevices();
    // A device pairs/connects (or drops) out of band via the desktop router, so
    // poll to reflect online status without a manual reload.
    const timer = setInterval(() => void loadDevices(), 5000);
    return () => clearInterval(timer);
  }, [loadDevices]);

  const loadTasks = useCallback(async () => {
    if (!client || !account || !deviceId) {
      setTasks([]);
      return;
    }
    try {
      const { tasks: list } = await client.listDeviceTasks(account.id, deviceId);
      setTasks(list);
    } catch {
      setTasks([]);
    }
  }, [client, account, deviceId]);

  // Fetch the selected device's recent tasks once it's online.
  useEffect(() => {
    if (selected?.online) void loadTasks();
    else setTasks([]);
  }, [selected?.online, loadTasks]);

  // Header "new chat": drop the active task and start fresh.
  useEffect(() => {
    if (resetSignal === 0) return;
    setActiveTask(null);
    setConversationId(null);
    setMessages([]);
    setApprovals([]);
    setError(null);
  }, [resetSignal]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-grow the composer textarea with its content (capped by CSS max-height).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  useEffect(() => {
    return () => {
      const ws = wsRef.current;
      if (ws) {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
      if (idleRef.current) clearTimeout(idleRef.current);
    };
  }, []);

  function finalize() {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    setStreaming(false);
    setMessages((cur) => cur.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    setApprovals([]);
    // The turn may have created or updated a thread on the device.
    void loadTasks();
  }

  function armIdle() {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(finalize, STREAM_WATCHDOG_MS);
  }

  // Open a task from the drawer: show its transcript and continue it in place.
  async function selectTask(task: LocalTask) {
    if (!client || !account || !deviceId || streaming) return;
    setActiveTask(task);
    setAgent(taskAgent(task));
    setConversationId(null);
    setApprovals([]);
    setMessages([]);
    setError(null);
    setHistoryLoading(true);
    try {
      const { messages: history } = await client.getDeviceTaskHistory(account.id, deviceId, task.detailKey);
      const restored: ChatMessage[] = [];
      for (const m of history) {
        if (m.role !== "user" && m.role !== "assistant") continue;
        const text = transcriptBody(m);
        if (!text) continue;
        restored.push({ id: `h-${restored.length}`, role: m.role, text });
      }
      setMessages(restored);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("local.historyFailed"));
    } finally {
      setHistoryLoading(false);
    }
  }

  async function decideApproval(approvalId: string, decision: "approve" | "reject") {
    if (!client || !account) return;
    setApprovals((cur) => cur.filter((a) => a.id !== approvalId));
    try {
      if (decision === "approve") await client.approveApproval(account.id, approvalId);
      else await client.rejectApproval(account.id, approvalId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    }
  }

  async function send() {
    if (!client || !account || !selected || streaming || !canContinue) return;
    const typed = input.trim();
    if (!typed) return;
    if (!selected.online) {
      setError(t("local.deviceOffline"));
      return;
    }

    setError(null);
    setInput("");
    const assistantId = `a-${Date.now()}`;
    setMessages((cur) => [
      ...cur,
      { id: `u-${Date.now()}`, role: "user", text: typed },
      { id: assistantId, role: "assistant", text: "", streaming: true }
    ]);
    setStreaming(true);

    try {
      const { session } = await client.startSession(
        account.id,
        { executionTarget: "local_device", deviceId: selected.id, agent },
        typed,
        conversationId,
        // First turn continues the picked task; later turns ride the
        // conversation (the backend resumes the thread the device reported).
        conversationId ? null : activeTask?.id
      );
      if (!conversationId && session.conversationId) setConversationId(session.conversationId);

      const ws = new WebSocket(client.sessionStreamUrl(account.id, session.id, 0));
      wsRef.current = ws;
      armIdle();
      ws.onmessage = (msg) => {
        armIdle();
        const event = JSON.parse(msg.data as string) as SessionEvent;
        if (event.type === "process.stdout" || event.type === "process.stderr" || event.type === "assistant.text") {
          const delta = eventText(event);
          if (delta) setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, text: m.text + delta } : m)));
        } else if (event.type === "assistant.final") {
          const text = eventText(event);
          if (text) setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, text } : m)));
        } else if (event.type === "approval.requested") {
          const p = event.payload as { approvalId?: string; title?: string; body?: string };
          if (p.approvalId) {
            setApprovals((cur) => [...cur, { id: p.approvalId!, title: p.title ?? t("local.approvalTitle"), body: p.body ?? "" }]);
          }
        } else if (event.type === "approval.approved" || event.type === "approval.rejected") {
          const p = event.payload as { approvalId?: string };
          if (p.approvalId) setApprovals((cur) => cur.filter((a) => a.id !== p.approvalId));
        } else if (event.type === "session.completed" || event.type === "session.failed" || event.type === "session.cancelled") {
          if (event.type === "session.failed") {
            const p = event.payload as { message?: string; code?: string };
            setMessages((cur) =>
              cur.map((m) => (m.id === assistantId ? { ...m, text: m.text || `⚠ ${failureText(p, t) ?? t("agent.error")}` } : m))
            );
          }
          finalize();
        }
      };
      ws.onerror = () => armIdle();
      ws.onclose = () => finalize();
    } catch (e) {
      setMessages((cur) =>
        cur.map((m) => (m.id === assistantId ? { ...m, text: `⚠ ${e instanceof Error ? e.message : t("common.failed")}`, streaming: false } : m))
      );
      setStreaming(false);
    }
  }

  if (loading) {
    return (
      <div className="center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <main className="main chat">
      {error ? <div className="error-banner">{error}</div> : null}

      <section className="panel">
        <div className="local-devices-head">
          <h2 className="section-title">{t("local.devicesTitle")}</h2>
          <div className="local-devices-actions">
            <button type="button" className="btn-secondary" onClick={() => void loadDevices()}>
              {t("local.refresh")}
            </button>
          </div>
        </div>

        {devices.length === 0 ? (
          <p className="muted">{t("local.noDevices")}</p>
        ) : (
          <ul className="device-list">
            {devices
              .filter((d) => d.status !== "revoked")
              .map((d) => (
                <li key={d.id}>
                  <label className="device-row">
                    <input
                      type="radio"
                      name="device"
                      checked={d.id === deviceId}
                      onChange={() => setDeviceId(d.id)}
                      disabled={!d.online}
                    />
                    <span className={`status-dot ${d.online ? "online" : "offline"}`} />
                    <span className="device-name">{d.name}</span>
                    <span className="muted device-platform">{d.platform}</span>
                    <span className="muted">{d.online ? t("local.online") : t("local.offline")}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </section>

      {activeTask ? (
        <div className="continuing-bar">
          <span className="continuing-chip" title={activeTask.title}>
            {t("local.continuing")}
            {activeTask.title || "(untitled)"}
          </span>
          {!canContinue ? <span className="muted">{t("local.notResumable")}</span> : null}
        </div>
      ) : null}

      <div className="transcript">
        {historyLoading ? (
          <div className="center" style={{ height: 80 }}>
            <div className="spinner" />
          </div>
        ) : messages.length === 0 ? (
          <p className="muted empty-hint">
            {activeTask ? t("local.noHistory") : selected ? t("local.readyHint") : t("local.pickDevice")}
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`bubble ${m.role}`}>
              <span className="bubble-role">{m.role === "user" ? t("agent.roleYou") : t("agent.roleAgent")}</span>
              {m.text ? (
                m.role === "assistant" ? (
                  <MarkdownMessage text={m.text} streaming={m.streaming} />
                ) : (
                  <p className="bubble-text">
                    {m.text}
                    {m.streaming ? <span className="caret" /> : null}
                  </p>
                )
              ) : m.streaming ? (
                <p className="bubble-text thinking">
                  <span className="thinking-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <span className="thinking-label">{t("agent.thinking")}</span>
                </p>
              ) : null}
            </div>
          ))
        )}
        {approvals.map((a) => (
          <div key={a.id} className="approval-card">
            <strong>{a.title}</strong>
            {a.body ? <p className="muted">{a.body}</p> : null}
            <div className="approval-actions">
              <button type="button" className="btn-primary" onClick={() => void decideApproval(a.id, "approve")}>
                {t("local.approve")}
              </button>
              <button type="button" className="btn-secondary" onClick={() => void decideApproval(a.id, "reject")}>
                {t("local.reject")}
              </button>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="composer">
        <div className="composer-box">
          <textarea
            ref={textareaRef}
            className="composer-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={canContinue ? t("local.composerPlaceholder") : t("local.notResumable")}
            disabled={streaming || !selected || !canContinue}
            rows={1}
          />
          <div className="composer-toolbar">
            <button
              type="button"
              className="btn-primary composer-send"
              onClick={() => void send()}
              disabled={streaming || !selected || !input.trim() || !canContinue}
            >
              {streaming ? <span className="spinner inline" /> : t("agent.send")}
            </button>
          </div>
        </div>
      </div>

      <LocalHistoryDrawer
        open={drawerOpen}
        tasks={tasks}
        offline={!selected?.online}
        selectedKey={activeTask?.detailKey ?? null}
        onClose={onCloseDrawer}
        onSelect={(task) => void selectTask(task)}
        onNew={() => {
          setActiveTask(null);
          setConversationId(null);
          setMessages([]);
          setApprovals([]);
          setError(null);
        }}
      />
      <LocalSettingsModal open={settingsOpen} onClose={onCloseSettings} />
    </main>
  );
}
